// /sources/:id — config snapshot + recent sync runs. Minimal v1.

import { useEffect, useState } from "react";
import { getSource, listRuns } from "./api";
import { StatusBadge } from "./StatusBadge";
import type { SourceRecord, SourceSyncRun } from "./types";
import "./sources.css";

interface Props {
  id: string;
  onBack?(): void;
}

export function SourceDetailPage({ id, onBack }: Props) {
  const [source, setSource] = useState<SourceRecord | null>(null);
  const [runs, setRuns] = useState<SourceSyncRun[]>([]);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getSource(id), listRuns(id)])
      .then(([s, r]) => {
        if (cancelled) return;
        setSource(s);
        setRuns(r);
      })
      .catch((err) => { if (!cancelled) setError(err); });
    return () => { cancelled = true; };
  }, [id]);

  if (error) {
    return <div className="maisight-sources-page" role="alert">Failed to load: {error.message}</div>;
  }
  if (!source) {
    return <div className="maisight-sources-page" role="status">Loading…</div>;
  }

  return (
    <div className="maisight-sources-page">
      <header className="maisight-sources-page-header">
        {onBack ? (
          <button type="button" className="maisight-sources-btn maisight-sources-btn--ghost" onClick={onBack}>
            Back
          </button>
        ) : null}
        <h1>{source.type} · {source.id}</h1>
        <StatusBadge status={source.last_sync_status} />
      </header>
      <section>
        <h2>Config</h2>
        <pre className="maisight-sources-config">{JSON.stringify(source.config, null, 2)}</pre>
      </section>
      <section>
        <h2>Recent runs</h2>
        {runs.length === 0 ? (
          <p>No runs yet.</p>
        ) : (
          <table className="maisight-sources-runs" data-testid="runs-table">
            <thead>
              <tr><th>Started</th><th>Items listed</th><th>Changed</th><th>Deleted</th><th>Error</th></tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{r.started_at}</td>
                  <td>{r.items_listed}</td>
                  <td>{r.items_changed}</td>
                  <td>{r.items_deleted}</td>
                  <td>{r.error ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
